import { useEffect, useState, useCallback } from "react";
import * as React from "react";

//QUERIES

import { useQueryClient } from "@tanstack/react-query";
import { UseAssetsQuery, UseSaveTour } from "../../../api/calls/Queries";
import SceneDelConfirmPanel from "../SceneDelConfirmPanel";

//SVG ICONS
//TYPESCRIPT

import { Scene, TourData } from "../../../Types";
import {
  useAssetListStore,
  useCurrentSceneStore,
  useEditingElementStore,
  useMapEditorStore,
  useTourDataStore,
} from "../../../api/Zustand";

import FileSelectorButton from "../GuiElements/FileSelectorButton";
import { AddIcon, EditPencil, Trashbin } from "../../Icons/gui-elements-icons";
import { useCompassHeadingStore } from "../../../state management/zustand/gl-stores";
import { getLanguageFile } from "../../common/GetLanguageFile";
import { SaveButton } from "./UiElements";

export default SceneEditing;

function SceneEditing({ tourID }: { tourID: number }) {
  const { tourData } = useTourDataStore();

  const { mutateAsync: saveTour, isPending: isSaving } = UseSaveTour();

  const { setEditingElem } = useEditingElementStore();
  const { setOpen } = useAssetListStore();

  const { data: panos } = UseAssetsQuery(tourID, 1);
  const { data: audioList } = UseAssetsQuery(tourID, 3);

  const queryClient = useQueryClient();

  const [currentScene, setCurrentScene] = useState<Scene>();

  const { currentSceneID, setCurrentSceneID } = useCurrentSceneStore();

  const [isDeletingScene, setIsDeletingScene] = useState<boolean>(false);

  useEffect(() => {
    if (tourData && tourData.scenes.length > 0) {
      let current = tourData.scenes.find((one) => one.id === currentSceneID);
      setCurrentScene(current);
    }
    return;
  }, [currentSceneID, tourData]);

  const [tempTitle, setTempTitle] = React.useState<string>();

  useEffect(() => {
    if (currentScene) {
      setTempTitle(currentScene.title);
    }
  }, [currentScene]);

  const onTitleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene) =>
          scene.id === currentSceneID
            ? { ...scene, title: e.target.value }
            : scene
        ),
      });
    },
    [queryClient, tourData, tourID, currentSceneID]
  );

  // const onFovChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   queryClient.setQueryData(["GetTour", tourID], {
  //     ...tourData,
  //     scenes: tourData!.scenes.map((scene) =>
  //       scene.id === currentSceneID
  //         ? { ...scene, fov: e.target.valueAsNumber }
  //         : scene
  //     ),
  //   });
  // };

  const onRotationChange = React.useCallback(
    (rot: number) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene) =>
          scene.id === currentSceneID ? { ...scene, rotation: rot } : scene
        ),
      });
    },
    [queryClient, tourData, currentSceneID, tourID]
  );
  const onLockRotationChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene) =>
          scene.id === currentSceneID
            ? { ...scene, lockRotation: e.target.checked }
            : scene
        ),
      });
    },
    [tourData, tourID, queryClient, currentSceneID]
  );

  const onNorthChange = React.useCallback(
    (north: number) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene) =>
          scene.id === currentSceneID ? { ...scene, north: north } : scene
        ),
      });
    },
    [queryClient, tourID, currentSceneID, tourData]
  );

  const handleSceneDelete = React.useCallback(() => {
    let tempScenes = tourData!.scenes.filter(
      (scene) => scene.id !== currentSceneID
    );

    const newTourData: TourData | undefined = queryClient.setQueryData(
      ["GetTour", tourID],
      {
        ...tourData,
        scenes: tempScenes,
        groups: tourData!.groups.map((group) => {
          const newScenes = group.scenes!.filter(
            (scene: number) => scene !== currentSceneID
          );

          return { ...group, scenes: newScenes };
        }),
        // @ts-expect-error
        startingScene:
          tourData?.startingScene === currentSceneID
            ? tourData!.scenes.filter((sc) => sc.id !== currentSceneID)[0]?.id
            : tourData!.startingScene,
      }
    );

    saveTour(newTourData as TourData);

    const firstScene = newTourData?.scenes[0];

    setCurrentSceneID(firstScene?.id as number);
  }, [
    saveTour,
    currentSceneID,
    queryClient,
    setCurrentSceneID,
    tourData,
    tourID,
  ]);

  const onGroupAdd = useCallback(() => {
    const newTourID = Math.floor(100000 + Math.random() * 900000);

    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData!.scenes.map((scene) =>
        scene.id === currentSceneID ? { ...scene, group: newTourID } : scene
      ),
      groups: [
        ...tourData!.groups,
        {
          id: newTourID,
          name: "Nuovo gruppo",
          scenes: [currentSceneID],
          mainColor: "#1c64f2",
          textColor: "#fff",
        },
      ],
    });

    setEditingElem("groups", newTourID);
  }, [queryClient, tourData, tourID, currentSceneID, setEditingElem]);

  const handleGroupAssign = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData!.scenes.map((scene) =>
          scene.id === currentSceneID
            ? { ...scene, group: JSON.parse(e.target.value) }
            : scene
        ),
        groups: tourData!.groups.map((group) =>
          group.id === JSON.parse(e.target.value)
            ? { ...group, scenes: [...(group.scenes || []), currentSceneID] }
            : {
                ...group,
                scenes: group.scenes!.filter(
                  (scene: number) => scene !== currentSceneID
                ),
              }
        ),
      });
    },
    [currentSceneID, queryClient, tourData, tourID]
  );

  const mapEditor = useMapEditorStore();

  const displayedAudioTitle: string | undefined = React.useMemo(() => {
    if (!currentScene?.audio) {
      return getLanguageFile("right_panel.add_audio");
    } else if (
      audioList?.find((one) => one.assetId === currentScene?.audio)
        ?.assetDescription
    )
      return audioList?.find((one) => one.assetId === currentScene?.audio)
        ?.assetDescription;
    else return getLanguageFile("audio_name_error");
  }, [currentScene?.audio, audioList]);

  const scenePanoName: string | undefined = React.useMemo(() => {
    let name;
    if (!currentScene?.imageID && !currentScene?.imageFull) {
      name = getLanguageFile("right_panel.select");
    } else
      name = panos?.find(
        (one) =>
          one.assetId === currentScene.imageID ||
          one.assetKeyFull === currentScene.imageFull
      )?.assetDescription;

    return name;
  }, [currentScene, panos]);

  const onAudioRemoval = React.useCallback(() => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData?.scenes.map((sc) =>
        sc.id === currentSceneID ? { ...sc, audio: undefined } : sc
      ),
    });
  }, [tourID, queryClient, tourData, currentSceneID]);

  const { headingDeg } = useCompassHeadingStore();

  return (
    <>
      {isDeletingScene && (
        <SceneDelConfirmPanel
          setIsDeletingScene={setIsDeletingScene}
          handleSceneDelete={handleSceneDelete}
        >
          <div>
            {getLanguageFile("right_panel.confirm_delete_scene")}{" "}
            <b>{currentScene?.title}?</b>
          </div>
        </SceneDelConfirmPanel>
      )}
      <div
        className="right-side-panel-editor"
        style={{
          height: "100%",
          overflowY: "auto",
        }}
      >
        {tourData && panos && currentScene ? (
          <div>
            {currentScene?.imageFull || currentScene?.imageID ? null : (
              <h4>{getLanguageFile("right_panel.add_image")}</h4>
            )}

            <form
              autoComplete="off"
              className="scene-data"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div>
                <label htmlFor="title">
                  {getLanguageFile("right_panel.title")}
                </label>
                <input
                  required
                  maxLength={24}
                  type="text"
                  id="title"
                  name="title"
                  value={tempTitle}
                  onChange={(e) => {
                    onTitleChange(e);
                    setTempTitle(e.target.value);
                  }}
                />
              </div>
              <label htmlFor="pano">
                {getLanguageFile("right_panel.pano")}
              </label>
              <FileSelectorButton id="pano" onClickFunction={() => setOpen(1)}>
                {scenePanoName}
              </FileSelectorButton>
              {currentScene?.imageID && !currentScene?.isUsingNewPano ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      <label>
                        {getLanguageFile("right_panel.rotation")}{" "}
                        <div style={{ display: "flex" }}>
                          <input
                            type="range"
                            id="Rotation"
                            min={0}
                            max={360}
                            step={1}
                            value={currentScene?.rotation}
                            onChange={(e) =>
                              onRotationChange(e.target.valueAsNumber)
                            }
                          />
                          <span>{currentScene?.rotation}</span>°
                        </div>
                      </label>
                    </div>
                    <div>
                      <div>
                        <label
                          htmlFor="lock-rotation"
                          style={{
                            marginBottom: "10px",
                          }}
                        >
                          <input
                            type="checkbox"
                            id="lock-rotation"
                            name="lock-rotation"
                            checked={currentScene?.lockRotation ?? false}
                            onChange={(e) => onLockRotationChange(e)}
                          />

                          {getLanguageFile("right_panel.rotation_lock")}
                        </label>
                      </div>
                    </div>
                    <div>
                      <label>
                        {/* Direzione nord{" "} */}
                        {getLanguageFile("right_panel.nord")}{" "}
                      </label>
                      <div style={{ display: "flex" }}>
                        <input
                          type="range"
                          id="north"
                          max={360}
                          min={0}
                          value={currentScene?.north}
                          onChange={(e) =>
                            onNorthChange(e.target.valueAsNumber)
                          }
                        />
                        {/* <button
                          onClick={() => onNorthChange(headingDeg)}
                          type="button"
                          >
                          {getLanguageFile("right_panel.current")}
                          </button> */}
                        <span style={{ fontWeight: 800 }}>
                          {currentScene?.north}
                        </span>
                        °
                      </div>
                    </div>
                  </div>

                  <div className="category-divider">
                    <h4>{getLanguageFile("right_panel.audio")}</h4>
                    <hr />
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    {/* {audioList && currentScene?.audio && (
                      <AudioPlayerThingy
                        small
                        audioList={audioList}
                        currentAudio={currentScene.audio}
                      />
                    )} */}
                    <FileSelectorButton
                      id="audio"
                      onClickFunction={() => setOpen(3)}
                    >
                      {displayedAudioTitle ?? getLanguageFile("generics.error")}
                    </FileSelectorButton>

                    {currentScene?.audio && (
                      <button
                        type="button"
                        style={{
                          border: "none",
                          minWidth: 0,
                          margin: 0,
                          padding: 5,
                        }}
                        onClick={() => {
                          onAudioRemoval();
                          setCurrentScene({
                            ...currentScene,
                            audio: undefined,
                          });
                        }}
                      >
                        <Trashbin width={"1.1rem"} height={"1.1rem"} />
                      </button>
                    )}
                  </div>
                  <div className="category-divider">
                    <h4>{getLanguageFile("right_panel.map")}</h4>
                    <hr />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      alignItems: "center",
                    }}
                  >
                    <select
                      onChange={(e) => {
                        queryClient.setQueryData(["GetTour", tourID], {
                          ...tourData,
                          scenes: tourData.scenes.map((sc) =>
                            sc.id === currentSceneID
                              ? { ...sc, map: parseInt(e.target.value) }
                              : sc
                          ),
                        });
                      }}
                      value={currentScene?.map}
                    >
                      <option value={undefined}>
                        {getLanguageFile("right_panel.no_map")}
                      </option>
                      {tourData.maps?.map((map) => (
                        <option key={map.id} value={map.id}>
                          {map.title}
                        </option>
                      ))}
                    </select>

                    {currentScene?.map ? (
                      <button
                        type="button"
                        className="round"
                        onClick={() => {
                          mapEditor.setOpen(currentScene.map!, true);
                        }}
                      >
                        <EditPencil width={".9rem"} height={".9rem"} />
                      </button>
                    ) : null}
                  </div>
                  {currentScene.map && (
                    <label>
                      <input
                        type="checkbox"
                        disabled={!currentScene?.map}
                        checked={currentScene?.mapAutoOpen || false}
                        onChange={(e) => {
                          queryClient.setQueryData(["GetTour", tourID], {
                            ...tourData,
                            scenes: tourData.scenes.map((sc) =>
                              sc.id === currentSceneID
                                ? { ...sc, mapAutoOpen: e.target.checked }
                                : sc
                            ),
                          });
                        }}
                      />
                      {getLanguageFile("right_panel.auto_open")}
                    </label>
                  )}
                  <div className="category-divider">
                    <h4>{getLanguageFile("right_panel.group")}</h4>
                    <hr />
                  </div>
                  <div
                    style={{
                      padding: "0",
                      margin: "0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "5px",
                        alignItems: "center",
                      }}
                    >
                      <select
                        value={currentScene?.group ?? "default"}
                        onChange={handleGroupAssign}
                      >
                        {JSON.stringify(currentScene?.group)}

                        {!currentScene?.group && (
                          <option value={"default"} disabled>
                            {/* Assegna ad un gruppo */}
                            {getLanguageFile("right_panel.add_to_group")}
                          </option>
                        )}

                        {tourData!.groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name} - {group.scenes!.length} Scen
                            {group.scenes!.length === 1 ? "a" : "e"}
                          </option>
                        ))}
                      </select>

                      <button
                        className="round"
                        type="button"
                        // id="small"

                        onClick={() => {
                          if (currentScene?.group) {
                            setEditingElem("groups", currentScene.group);
                          } else onGroupAdd();
                        }}
                      >
                        {currentScene?.group ? (
                          <EditPencil width={".9rem"} height={".9rem"} />
                        ) : (
                          <AddIcon width={"2rem"} height={"2rem"} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div
                    className="buttons-wrapper"
                    style={{
                      justifySelf: "flex-end",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        // flexDirection: "column",
                      }}
                    >
                      <button
                        id="delete"
                        type="button"
                        style={{ flex: 1 }}
                        disabled={isSaving}
                        onClick={() => setIsDeletingScene(true)}
                      >
                        {getLanguageFile("generics.delete")}
                        <Trashbin />
                      </button>

                      <SaveButton
                        savingBool={isSaving}
                        isTemp={currentScene.isNew!}
                        disabled={isSaving}
                        type="submit"
                        id="save"
                        onClick={() => saveTour(tourData!)}
                      ></SaveButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <button id="delete" onClick={() => handleSceneDelete()}>
                    {getLanguageFile("right_panel.cancel_scene_creation")}
                  </button>
                </>
              )}
            </form>
          </div>
        ) : null}
      </div>
    </>
  );
}
